import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Box,
  Clock,
  MoreVertical,
  Trash2,
  Edit3,
  Eye,
  Loader2,
  FolderOpen,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useDesigns } from '@/hooks/useDesigns'
import { useProject } from '@/hooks/useProjects'
import { formatDistanceToNow } from '@/lib/utils'
import type { Design } from '@/lib/database.types'

interface ProjectDetailsViewProps {
  projectId: string
  onBack: () => void
  onSelectDesign: (designId: string, designUrl: string | null) => void
}

export default function ProjectDetailsView({ 
  projectId, 
  onBack, 
  onSelectDesign 
}: ProjectDetailsViewProps) {
  const { data: project, isLoading: projectLoading } = useProject(projectId)
  const { designs, isLoading: designsLoading, deleteDesign } = useDesigns(projectId)
  const [menuOpen, setMenuOpen] = useState<string | null>(null)

  const isLoading = projectLoading || designsLoading

  const handleDeleteDesign = async (designId: string) => {
    if (confirm('Are you sure you want to delete this design?')) {
      await deleteDesign(designId)
    }
    setMenuOpen(null)
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Button>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${project?.color || '#6366f1'}20` }}
          >
            <FolderOpen
              className="w-6 h-6"
              style={{ color: project?.color || '#6366f1' }}
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{project?.name || 'Project'}</h1>
            <p className="text-surface-400">{designs.length} design{designs.length === 1 ? '' : 's'}</p>
          </div>
        </div>

        {project?.description && (
          <p className="text-surface-400 mb-6">{project.description}</p>
        )}

        {/* Designs Grid */}
        {designs.length === 0 ? (
          <Card className="p-12 text-center">
            <Box className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No designs yet</h3>
            <p className="text-surface-400 mb-6">
              This project doesn't have any designs. Create one using Text to 3D or upload a model.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {designs.map((design: Design, index: number) => (
                <motion.div
                  key={design.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="group cursor-pointer hover:border-surface-600 transition-all overflow-hidden"
                    onClick={() => onSelectDesign(design.id, design.file_path)}
                  >
                    {/* Thumbnail */}
                    <div className="h-32 bg-surface-800 flex items-center justify-center">
                      <Box className="w-12 h-12 text-surface-600" />
                    </div>

                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate group-hover:text-primary-400 transition-colors">
                            {design.name}
                          </h3>
                          <p className="text-xs text-surface-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDistanceToNow(design.updated_at)}
                          </p>
                        </div>
                        
                        {/* Menu */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuOpen(menuOpen === design.id ? null : design.id)
                            }}
                            className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {menuOpen === design.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="absolute right-0 top-8 w-36 bg-surface-800 border border-surface-700 rounded-lg shadow-xl z-10 overflow-hidden"
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onSelectDesign(design.id, design.file_path)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-surface-300 hover:bg-surface-700 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setMenuOpen(null)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-surface-300 hover:bg-surface-700 flex items-center gap-2"
                              >
                                <Edit3 className="w-4 h-4" />
                                Rename
                              </button>
                              <div className="border-t border-surface-700" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteDesign(design.id)
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </motion.div>
                          )}
                        </div>
                      </div>

                      {design.ai_generated && (
                        <span className="inline-block px-2 py-0.5 text-xs bg-ai-500/20 text-ai-400 rounded">
                          AI Generated
                        </span>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  )
}
